"use client";

import { sendGAEvent } from "@next/third-parties/google";
import NextImage from "next/image";
import { memo, useCallback, useMemo } from "react";
import localise from "@/components/localise";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { prisjakt as prisjaktApi } from "@/config";
import { prisjaktProductType } from "@/types";

// Move formatters outside component to prevent recreation on each render
const moneyFormatter = new Intl.NumberFormat("nb-NO");
const percentageFormatter = new Intl.NumberFormat("nb-NO", {
  style: "percent",
  maximumFractionDigits: 0,
});

// Memoized subcomponents for better performance
const Percentage = memo(({ diffPercentage }) => {
  if (!diffPercentage || diffPercentage <= 0) {
    return null;
  }
  return (
    <Badge className="absolute top-3 left-4 bg-pink-200 text-sm">
      -{percentageFormatter.format(diffPercentage / 100)}
    </Badge>
  );
});

Percentage.displayName = "Percentage";

const UserReviews = memo(({ rating }) => (
  <Badge
    variant="secondary"
    className="absolute top-3 right-4 text-sm bg-white"
  >
    ★ {rating}
  </Badge>
));

UserReviews.displayName = "UserReviews";

const StoreLogo = memo(({ storeLogo, storeName }) => {
  if (!storeLogo || !storeName) return null;

  return (
    <NextImage
      src={storeLogo}
      alt={localise("prisjaktWidget.imageAlt", { storeName })}
      width={70.4}
      height={24.8}
      className="absolute bottom-2 left-0 p-1 bg-white/95"
      loading="lazy"
      sizes="71px"
      quality={75}
    />
  );
});

StoreLogo.displayName = "StoreLogo";

const PriceDisplay = memo(({ bestOffer }) => {
  if (!bestOffer?.price) return null;

  const formattedPrice = moneyFormatter.format(bestOffer.price);
  const hasOriginalPrice =
    bestOffer.originalPrice && bestOffer.originalPrice !== bestOffer.price;
  const formattedOriginalPrice = hasOriginalPrice
    ? moneyFormatter.format(bestOffer.originalPrice)
    : null;

  return (
    <div className="flex flex-row mt-auto text-lg">
      <span className="text-md font-medium text-pink-600">
        {localise("prisjaktWidget.newPrice", {
          offer: formattedPrice,
        })}
      </span>
      {hasOriginalPrice && (
        <span className="text-gray-500 ml-2 md:ml-1 font-medium line-through">
          {localise("prisjaktWidget.newPrice", {
            offer: formattedOriginalPrice,
          })}
        </span>
      )}
    </div>
  );
});

PriceDisplay.displayName = "PriceDisplay";

const StoreInfo = memo(({ bestOffer, storeCount }) => {
  if (storeCount <= 0 || !bestOffer?.storeName) return null;

  return (
    <div className="text-xs">
      <b>{bestOffer.storeName}</b>
      {storeCount > 1 && ` +${storeCount - 1} butikker`}
    </div>
  );
});

StoreInfo.displayName = "StoreInfo";

const PrisjaktProduct = memo(({ product }) => {
  const {
    name,
    productId,
    bestOffer,
    priceDrop,
    productLink,
    aggregatedRating,
    logo,
    categoryName,
    storeCount = 0,
    storeLogo,
  } = product || {};

  // Memoize calculated values to prevent unnecessary recalculations
  const calculatedValues = useMemo(() => {
    const diffPercentage = (() => {
      if (priceDrop) {
        return priceDrop;
      }
      if (!bestOffer?.originalPrice || !bestOffer?.price) return 0;
      const diff = bestOffer.originalPrice - bestOffer.price;
      return diff > 0 ? (diff / bestOffer.originalPrice) * 100 : 0;
    })();

    const ariaLabel = localise("prisjaktWidget.productTitle", { name });

    let productUrl =
      bestOffer?.featured && typeof bestOffer?.offerLink === "string"
        ? bestOffer?.offerLink
        : `https://www.prisjakt.no${productLink}`;

    productUrl = new URL(productUrl);
    productUrl.searchParams.set("ref", prisjaktApi.ref);

    return {
      diffPercentage,
      ariaLabel,
      productUrl,
    };
  }, [priceDrop, bestOffer, name, productLink]);

  // Optimize click handler with useCallback and proper dependencies
  const handleProductClick = useCallback(() => {
    try {
      sendGAEvent({
        event: "prisjaktProduct",
        value: `${productId} - ${name}`,
      });
    } catch (error) {
      console.warn("Failed to send GA event:", error);
    }
  }, [productId, name]);

  // Early return if critical data is missing (after hooks)
  if (!productId || !name || !bestOffer) {
    console.warn("PrisjaktProduct: Missing required product data", {
      productId,
      name,
      bestOffer,
    });
    return null;
  }

  return (
    <Card className="relative p-0 group border-none">
      <CardContent className="p-4 h-full flex flex-col">
        <figure className="relative">
          <NextImage
            src={logo}
            alt={name}
            width={280}
            height={280}
            className="w-full h-28 md:h-40 rounded-tl-md rounded-tr-md object-contain group-hover:scale-105 transition-transform duration-300 ease-in-out"
            loading="lazy"
            sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
            quality={75}
            placeholder="empty"
          />

          <StoreLogo storeLogo={storeLogo} storeName={bestOffer?.storeName} />

          <div className="absolute -top-4 -left-4 -right-4 -bottom-4 rounded-tl-md rounded-tr-md bg-gray-400/15" />
        </figure>

        <Percentage diffPercentage={calculatedValues.diffPercentage} />

        {categoryName && (
          <p className="leading-snug line-clamp-2 mt-8 text-xs text-gray-500">
            {categoryName}
          </p>
        )}

        <div className="flex gap-2 mb-2 justify-between">
          <p className="font-medium leading-snug line-clamp-2">{name}</p>
          {aggregatedRating?.score > 0 && (
            <UserReviews rating={aggregatedRating.score} />
          )}
        </div>

        <PriceDisplay bestOffer={bestOffer} />
        <StoreInfo bestOffer={bestOffer} storeCount={storeCount} />
      </CardContent>

      <a
        href={calculatedValues.productUrl}
        rel="nofollow noopener noreferrer"
        target="_blank"
        onClick={handleProductClick}
        className="absolute top-0 left-0 right-0 bottom-0"
        aria-label={calculatedValues.ariaLabel}
      >
        <span className="sr-only">{calculatedValues.ariaLabel}</span>
      </a>
    </Card>
  );
});

PrisjaktProduct.displayName = "PrisjaktProduct";
PrisjaktProduct.propTypes = prisjaktProductType;

export default PrisjaktProduct;
